export type WrapperInfo = {
  id: string;
  tag: string;
  attributes: [string, string][];
};

export type Token =
  | {
      type: "text";
      id: string;
      node: Text;
      content: string;
      meta?: Record<string, any>;
    }
  | {
      type: "space";
      id: string;
      node: Text;
      content: string;
      meta?: Record<string, any>;
    }
  | {
      type: "element";
      id: string;
      node: HTMLElement;
      tagName: string;
      meta?: Record<string, any>;
    }
  | {
      type: "br";
      id: string;
      node: HTMLElement;
      tagName: "br";
      meta?: Record<string, any>;
    }
  | {
      type: "other";
      id: string;
      node: Node;
      meta?: Record<string, any>;
    };

const OPAQUE_TAGS = new Set([
  "img", "video", "audio", "canvas", "iframe", "object", "svg",
  "input", "textarea", "select", "button",
  "area", "base", "col", "embed", "hr", "link", "meta", "param",
  "source", "track", "wbr", "picture", "table",
]);

function getElementAttributes(el: HTMLElement): [string, string][] {
  const attrs: [string, string][] = [];
  for (let i = 0; i < el.attributes.length; i++) {
    const attr = el.attributes[i];
    attrs.push([attr.name, attr.value]);
  }
  return attrs;
}

let tokenCounter = 0;

export function BuildTokens(nodes: NodeListOf<ChildNode>): Token[] {
  tokenCounter = 0;
  const tokens: Token[] = [];

  const pushWithMeta = (t: Token, extra: Record<string, any> | undefined) => {
    if (extra && Object.keys(extra).length) {
      t.meta = { ...(t.meta || {}), ...extra };
    }
    tokens.push(t);
  };

  const processNode = (node: ChildNode, carryMeta?: Record<string, any>) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();

      if (tag === "split-class") {
        const classes = (el.getAttribute("class") ?? "").split(/\s+/).filter(Boolean);
        const extraMeta = {
          ...(carryMeta || {}),
          splitClass: [...((carryMeta?.splitClass as string[]) ?? []), ...classes],
        };
        el.childNodes.forEach((child) => processNode(child as ChildNode, extraMeta));
        return;
      }

      if (tag === "br") {
        pushWithMeta(
          {
            type: "br",
            id: `br_${tokenCounter++}`,
            node: el,
            tagName: "br",
          },
          carryMeta
        );
        return;
      }

      if (!OPAQUE_TAGS.has(tag) && el.childNodes.length > 0) {
        const wrapperInfo: WrapperInfo = {
          id: `wrapper_${tokenCounter++}`,
          tag,
          attributes: getElementAttributes(el),
        };
        const currentWrappers: WrapperInfo[] = (carryMeta?.wrappers as WrapperInfo[]) ?? [];
        const extraMeta = {
          ...(carryMeta || {}),
          wrappers: [...currentWrappers, wrapperInfo],
        };
        el.childNodes.forEach((child) => processNode(child as ChildNode, extraMeta));
        return;
      }

      pushWithMeta(
        {
          type: "element",
          id: `el_${tokenCounter++}`,
          node: el,
          tagName: tag,
        },
        carryMeta
      );
      return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.nodeValue ?? "";
      const id = `text_${tokenCounter++}`;
      if (text.trim()) {
        pushWithMeta({ type: "text", id, node: node as Text, content: text }, carryMeta);
      } else {
        pushWithMeta({ type: "space", id, node: node as Text, content: text }, carryMeta);
      }
      return;
    }

    pushWithMeta({ type: "other", id: `node_${tokenCounter++}`, node }, carryMeta);
  };

  nodes.forEach((n) => processNode(n as ChildNode));
  return tokens;
}
